const ADMIN_BASE = process.env.REACT_APP_ADMIN_PATH || '/admin';
import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, LogOut, Loader2, BedDouble, HelpCircle, MapPinned, ExternalLink, Mail, KeyRound, Download, Check, Megaphone, Link2, Inbox, TrendingUp } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  adminGetHotels, adminCreateHotel, adminUpdateHotel, adminDeleteHotel,
  adminGetFaqs, adminCreateFaq, adminUpdateFaq, adminDeleteFaq,
  adminGetEmirates, adminCreateEmirate, adminUpdateEmirate, adminDeleteEmirate,
  adminGetNewsletter, adminChangePassword,
  adminGetAffiliates, adminCreateAffiliate, adminUpdateAffiliate, adminDeleteAffiliate,
  adminGetMonetization, adminUpdateMonetization, adminGetLeads, adminDeleteLead,
  adminGetAffiliateClicks,
} from '../api';

const BUDGET_OPTS = [
  { value: 'luxury', label: 'Luxury' },
  { value: 'mid', label: 'Mid-range' },
  { value: 'budget', label: 'Budget' },
];
const PAGE_OPTS = [
  { value: 'visas', label: 'Visas & Entry' },
  { value: 'hotels', label: 'Hotels' },
  { value: 'living', label: 'Living' },
];

const HOTEL_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'emirate', label: 'Emirate' },
  { key: 'area', label: 'Area / neighbourhood' },
  { key: 'budget', label: 'Budget', type: 'select', options: BUDGET_OPTS },
  { key: 'tripTypes', label: 'Trip types (comma separated)', type: 'csv' },
  { key: 'pricePerNight', label: 'Price per night (AUD number)', type: 'number' },
  { key: 'priceLabel', label: 'Price label (e.g. AUD $310 / night)' },
  { key: 'desc', label: 'Description', type: 'textarea' },
  { key: 'bookingVia', label: 'Booking via (e.g. Booking.com)' },
  { key: 'image', label: 'Image URL' },
  { key: 'lat', label: 'Latitude', type: 'number' },
  { key: 'lng', label: 'Longitude', type: 'number' },
  { key: 'featured', label: 'Featured (paid placement — ranks first)', type: 'bool' },
];
const FAQ_FIELDS = [
  { key: 'page', label: 'Page', type: 'select', options: PAGE_OPTS },
  { key: 'q', label: 'Question' },
  { key: 'a', label: 'Answer', type: 'textarea' },
];
const EMIRATE_FIELDS = [
  { key: 'slug', label: 'Slug (e.g. dubai)' },
  { key: 'number', label: 'Number (e.g. 01)' },
  { key: 'name', label: 'Name' },
  { key: 'tagline', label: 'Tagline', type: 'textarea' },
  { key: 'image', label: 'Image URL' },
  { key: 'bestFor', label: 'Best for (comma separated text)' },
];
const AFFILIATE_FIELDS = [
  { key: 'name', label: 'Partner name' },
  { key: 'category', label: 'Category', type: 'select', options: [
    { value: 'hotels', label: 'Hotels' }, { value: 'tours', label: 'Tours' }, { value: 'insurance', label: 'Insurance' },
    { value: 'esim', label: 'eSIM' }, { value: 'flights', label: 'Flights' }, { value: 'finance', label: 'Finance' },
    { value: 'transfers', label: 'Transfers' }, { value: 'other', label: 'Other' },
  ] },
  { key: 'url', label: 'Affiliate URL (paste your tracking link here)' },
  { key: 'cta', label: 'Button text' },
  { key: 'blurb', label: 'Short description', type: 'textarea' },
  { key: 'priceLabel', label: 'Price label (optional, e.g. from ~AUD $64/mo)' },
  { key: 'badge', label: 'Promo ribbon (optional)', type: 'select', options: [
    { value: '', label: 'None' }, { value: 'Deal of the week', label: 'Deal of the week' },
    { value: 'Limited time', label: 'Limited time' }, { value: 'Seasonal offer', label: 'Seasonal offer' },
    { value: 'Flash sale', label: 'Flash sale' }, { value: 'Editor’s pick', label: 'Editor’s pick' },
  ] },
  { key: 'badgeExpiry', label: 'Ribbon expiry date (optional — hides the ribbon after this date)', type: 'date' },
  { key: 'placements', label: 'Placements (comma separated: home, emirate, visas, hotels, living, business, property)', type: 'csv' },
  { key: 'order', label: 'Sort order (number)', type: 'number' },
  { key: 'enabled', label: 'Status', type: 'bool', boolDefault: 'true' },
];

function EditModal({ title, fields, initial, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    const base = {};
    fields.forEach((f) => {
      const v = initial ? initial[f.key] : undefined;
      if (f.type === 'csv') base[f.key] = Array.isArray(v) ? v.join(', ') : v || '';
      else if (f.type === 'bool') base[f.key] = v === true ? 'true' : v === false ? 'false' : (f.boolDefault || 'false');
      else base[f.key] = v ?? '';
    });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr('');
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.key];
      if (f.type === 'csv') v = String(v).split(',').map((s) => s.trim()).filter(Boolean);
      else if (f.type === 'number') v = v === '' ? null : Number(v);
      else if (f.type === 'bool') v = v === 'true' || v === true;
      payload[f.key] = v;
    });
    try {
      await onSave(payload);
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not save. Please check the fields.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-[#0b1a2b]/60 backdrop-blur-sm p-4 sm:p-8">
      <div className="w-full max-w-lg my-8 bg-[#F6F1E3] rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6D7A2]">
          <h3 className="font-display text-[20px] font-semibold text-[#1B1D21]">{title}</h3>
          <button onClick={onClose} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-[#F2ECCF] text-[#1B1D21]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[13px] font-semibold text-[#45596b] mb-1.5">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={3}
                  className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]"
                />
              ) : f.type === 'select' ? (
                <select
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]"
                >
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === 'bool' ? (
                <select
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]"
                >
                  <option value="true">Enabled (live on site)</option>
                  <option value="false">Disabled (hidden)</option>
                </select>
              ) : (
                <input
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  step="any"
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]"
                />
              )}
            </div>
          ))}
          {err ? <p className="text-[14px] text-[#d6453c]">{err}</p> : null}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-ink rounded-full px-5 py-2.5 text-[14px] font-semibold">Cancel</button>
            <button type="submit" disabled={saving} className="btn-sunset inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[14px] font-semibold disabled:opacity-70">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'hotels', label: 'Hotels', icon: BedDouble },
  { key: 'faqs', label: 'FAQs', icon: HelpCircle },
  { key: 'emirates', label: 'Emirates', icon: MapPinned },
  { key: 'affiliates', label: 'Affiliates', icon: Link2 },
  { key: 'ads', label: 'Ads', icon: Megaphone },
  { key: 'leads', label: 'Leads', icon: Inbox },
  { key: 'subscribers', label: 'Subscribers', icon: Mail },
  { key: 'settings', label: 'Settings', icon: KeyRound },
];
const CONTENT_TABS = ['hotels', 'faqs', 'emirates', 'affiliates'];

const AdminDashboard = () => {
  const { token, username, isAuthed, ready, logout } = useAdminAuth();
  const [tab, setTab] = useState('hotels');
  const [hotels, setHotels] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [clickStats, setClickStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {entity, item}
  const [subscribers, setSubscribers] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [ads, setAds] = useState(null);
  const [adsState, setAdsState] = useState({ status: 'idle', msg: '' });
  const [cp, setCp] = useState({ current: '', next: '', confirm: '' });
  const [cpState, setCpState] = useState({ status: 'idle', msg: '' });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [h, f, e, a] = await Promise.all([
        adminGetHotels(token), adminGetFaqs(token), adminGetEmirates(token), adminGetAffiliates(token),
      ]);
      setHotels(h); setFaqs(f); setEmirates(e); setAffiliates(a);
      adminGetAffiliateClicks(token).then((d) => setClickStats(d.items || [])).catch(() => {});
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (isAuthed) load(); }, [isAuthed, load]);

  const loadLeads = useCallback(async () => {
    if (!token) return;
    setLeadsLoading(true);
    try { const d = await adminGetLeads(token); setLeads(d.leads || []); } catch (e) {}
    finally { setLeadsLoading(false); }
  }, [token]);

  const loadAds = useCallback(async () => {
    if (!token) return;
    try { const d = await adminGetMonetization(token); setAds(d); } catch (e) {}
  }, [token]);

  useEffect(() => { if (isAuthed && tab === 'leads') loadLeads(); }, [isAuthed, tab, loadLeads]);
  useEffect(() => { if (isAuthed && tab === 'ads' && !ads) loadAds(); }, [isAuthed, tab, ads, loadAds]);

  const loadSubs = useCallback(async () => {
    if (!token) return;
    setSubsLoading(true);
    try {
      const data = await adminGetNewsletter(token);
      setSubscribers(data.subscribers || []);
    } catch (e) { /* ignore */ }
    finally { setSubsLoading(false); }
  }, [token]);

  useEffect(() => { if (isAuthed && tab === 'subscribers') loadSubs(); }, [isAuthed, tab, loadSubs]);

  const downloadCsv = () => {
    const rows = [['email', 'subscribed_at'], ...subscribers.map((s) => [s.email, s.created_at || ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uae-newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (cp.next !== cp.confirm) {
      setCpState({ status: 'error', msg: 'New passwords do not match.' });
      return;
    }
    setCpState({ status: 'saving', msg: '' });
    try {
      const res = await adminChangePassword(token, cp.current, cp.next);
      setCpState({ status: 'done', msg: res.message || 'Password updated.' });
      setCp({ current: '', next: '', confirm: '' });
    } catch (e2) {
      setCpState({ status: 'error', msg: e2?.response?.data?.detail || 'Could not update password.' });
    }
  };

  if (ready && !isAuthed) return <Navigate to={`${ADMIN_BASE}/login`} replace />;

  const saveHandlers = {
    hotels: {
      create: (p) => adminCreateHotel(token, p),
      update: (id, p) => adminUpdateHotel(token, id, p),
    },
    faqs: {
      create: (p) => adminCreateFaq(token, p),
      update: (id, p) => adminUpdateFaq(token, id, p),
    },
    emirates: {
      create: (p) => adminCreateEmirate(token, p),
      update: (slug, p) => adminUpdateEmirate(token, slug, p),
    },
    affiliates: {
      create: (p) => adminCreateAffiliate(token, p),
      update: (id, p) => adminUpdateAffiliate(token, id, p),
    },
  };

  const idOf = (entity, item) => (entity === 'emirates' ? item.slug : item.id);

  const onSave = async (entity, item, payload) => {
    if (item) await saveHandlers[entity].update(idOf(entity, item), payload);
    else await saveHandlers[entity].create(payload);
    await load();
  };

  const onDelete = async (entity, item) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    if (entity === 'hotels') await adminDeleteHotel(token, item.id);
    if (entity === 'faqs') await adminDeleteFaq(token, item.id);
    if (entity === 'emirates') await adminDeleteEmirate(token, item.slug);
    if (entity === 'affiliates') await adminDeleteAffiliate(token, item.id);
    await load();
  };

  const saveAds = async (e) => {
    e.preventDefault();
    setAdsState({ status: 'saving', msg: '' });
    try {
      const saved = await adminUpdateMonetization(token, {
        adsEnabled: ads.adsEnabled,
        adsenseClient: ads.adsenseClient || '',
        adSlots: ads.adSlots || {},
        leadgenEnabled: ads.leadgenEnabled,
        alertEmail: ads.alertEmail || '',
        confirmationEnabled: ads.confirmationEnabled !== false,
        confirmationMessage: ads.confirmationMessage || '',
      });
      setAds(saved);
      setAdsState({ status: 'done', msg: 'Saved. Changes are live on the site.' });
    } catch (e2) {
      setAdsState({ status: 'error', msg: 'Could not save settings.' });
    }
  };

  const downloadLeadsCsv = () => {
    const rows = [['type', 'name', 'email', 'phone', 'message', 'date'],
      ...leads.map((l) => [l.type, l.name, l.email, l.phone || '', (l.message || '').replace(/\n/g, ' '), l.created_at || ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `uae-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    setLeads((p) => p.filter((l) => l.id !== id));
    try { await adminDeleteLead(token, id); } catch (e) { loadLeads(); }
  };

  const fieldsFor = { hotels: HOTEL_FIELDS, faqs: FAQ_FIELDS, emirates: EMIRATE_FIELDS, affiliates: AFFILIATE_FIELDS };
  const titleFor = { hotels: 'Hotel', faqs: 'FAQ', emirates: 'Emirate', affiliates: 'Affiliate' };

  const clicksById = {};
  clickStats.forEach((c) => { clicksById[c.id] = c; });
  const topDeals = [...clickStats].filter((c) => c.clicks > 0).slice(0, 5);
  const totalClicks = clickStats.reduce((s, c) => s + (c.clicks || 0), 0);

  return (
    <div className="bg-[#F6F1E3] min-h-screen">
      {/* Top bar */}
      <div className="bg-[#1B1D21]">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
          <div>
            <span className="text-[12px] font-semibold tracking-[0.16em] uppercase text-[#CBA344]">Admin CMS</span>
            <h1 className="font-display text-[26px] font-semibold text-white">Content manager</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-[14px] text-[#c3d1de]">Signed in as <span className="font-semibold text-white">{username || 'admin'}</span></span>
            <Link to="/" className="text-[14px] text-[#c3d1de] hover:text-white inline-flex items-center gap-1">View site <ExternalLink className="w-4 h-4" /></Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-white/30 text-white hover:bg-white/10 px-4 py-2 text-[14px] font-semibold">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            const count = t.key === 'hotels' ? hotels.length : t.key === 'faqs' ? faqs.length : t.key === 'emirates' ? emirates.length : t.key === 'affiliates' ? affiliates.length : t.key === 'subscribers' ? subscribers.length : t.key === 'leads' ? leads.length : null;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-semibold transition-colors ${
                  active ? 'bg-[#1B1D21] text-white' : 'bg-white border border-[#E6D7A2] text-[#45596b] hover:border-[#92722A]'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label} {count !== null ? <span className={active ? 'text-[#CBA344]' : 'text-[#9aa7b3]'}>({count})</span> : null}
              </button>
            );
          })}
          {CONTENT_TABS.includes(tab) && (
            <button
              onClick={() => setModal({ entity: tab, item: null })}
              className="ml-auto btn-sunset inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold"
            >
              <Plus className="w-4 h-4" /> Add {titleFor[tab].toLowerCase()}
            </button>
          )}
        </div>

        {CONTENT_TABS.includes(tab) && (loading ? (
          <div className="py-24 flex justify-center text-[#92722A]"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {tab === 'hotels' && hotels.map((h) => (
              <Row key={h.id} title={h.name} subtitle={`${h.area}, ${h.emirate} · ${h.budgetLabel} · ${h.priceLabel}`}
                onEdit={() => setModal({ entity: 'hotels', item: h })} onDelete={() => onDelete('hotels', h)} />
            ))}
            {tab === 'faqs' && faqs.map((f) => (
              <Row key={f.id} title={f.q} subtitle={`${f.page} · ${f.a.slice(0, 90)}${f.a.length > 90 ? '…' : ''}`}
                onEdit={() => setModal({ entity: 'faqs', item: f })} onDelete={() => onDelete('faqs', f)} />
            ))}
            {tab === 'emirates' && emirates.map((e) => (
              <Row key={e.slug} title={`${e.number} · ${e.name}`} subtitle={e.tagline}
                onEdit={() => setModal({ entity: 'emirates', item: e })} onDelete={() => onDelete('emirates', e)} />
            ))}
            {tab === 'affiliates' && (
              <div data-testid="deal-clicks-panel" className="rounded-2xl bg-[#1B1D21] text-white p-6 mb-2">
                <div className="flex items-center gap-2 text-[#CBA344]">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-[12px] font-semibold tracking-[0.16em] uppercase">Deal clicks · your top earners</span>
                </div>
                <p className="mt-1 text-[14px] text-[#c3d1de]">{totalClicks} total click{totalClicks === 1 ? '' : 's'} across all deals.</p>
                {topDeals.length === 0 ? (
                  <p className="mt-3 text-[14px] text-[#9fb3c6]">No clicks tracked yet. Clicks on your Deals page and affiliate blocks will appear here.</p>
                ) : (
                  <div className="mt-4 space-y-2">
                    {topDeals.map((c, i) => (
                      <div key={c.id} className="flex items-center justify-between gap-4 rounded-xl bg-white/5 px-4 py-2.5">
                        <span className="flex items-center gap-2.5 min-w-0">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full sunset-gradient text-white text-[12px] font-bold shrink-0">{i + 1}</span>
                          <span className="font-display text-[16px] font-semibold text-white truncate">{c.name}</span>
                          <span className="text-[12px] text-[#9fb3c6] capitalize shrink-0">{c.category}</span>
                        </span>
                        <span className="flex items-center gap-3 shrink-0 text-[14px]">
                          <span className="font-semibold text-[#CBA344]">{c.clicks} clicks</span>
                          <span className="text-[#9fb3c6]">{c.clicks7d} this week</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {tab === 'affiliates' && affiliates.map((a) => {
              const st = clicksById[a.id] || { clicks: 0, clicks7d: 0 };
              return (
                <Row key={a.id} title={`${a.name} ${a.enabled ? '' : '· (disabled)'}`}
                  subtitle={`${a.category} · ${st.clicks} clicks (${st.clicks7d} this week) · ${(a.placements || []).join(', ') || 'no placements'}`}
                  onEdit={() => setModal({ entity: 'affiliates', item: a })} onDelete={() => onDelete('affiliates', a)} />
              );
            })}
          </div>
        ))}

        {/* Ads settings */}
        {tab === 'ads' && (
          <div className="max-w-xl">
            {!ads ? (
              <div className="py-16 flex justify-center text-[#92722A]"><Loader2 className="w-7 h-7 animate-spin" /></div>
            ) : (
              <form onSubmit={saveAds} className="rounded-2xl bg-white border border-[#E6D7A2] p-7 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)] space-y-5">
                <div className="flex items-center gap-2 text-[#e0564e]">
                  <Megaphone className="w-5 h-5" />
                  <span className="text-[12px] font-semibold tracking-[0.16em] uppercase">Google AdSense</span>
                </div>
                <label className="flex items-center justify-between gap-4 py-1">
                  <span className="text-[15px] font-medium text-[#1B1D21]">Show ads on the site</span>
                  <input type="checkbox" checked={!!ads.adsEnabled} onChange={(e) => setAds({ ...ads, adsEnabled: e.target.checked })} className="w-5 h-5 accent-[#e0564e]" />
                </label>
                <label className="flex items-center justify-between gap-4 py-1">
                  <span className="text-[15px] font-medium text-[#1B1D21]">Show lead-gen inquiry forms</span>
                  <input type="checkbox" checked={!!ads.leadgenEnabled} onChange={(e) => setAds({ ...ads, leadgenEnabled: e.target.checked })} className="w-5 h-5 accent-[#e0564e]" />
                </label>
                <div>
                  <label className="block text-[13px] font-semibold text-[#45596b] mb-1.5">Lead alert email</label>
                  <input value={ads.alertEmail || ''} onChange={(e) => setAds({ ...ads, alertEmail: e.target.value })}
                    className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]" placeholder="you@email.com — get an email on every new lead" />
                  <p className="mt-1 text-[12px] text-[#8a7a63]">Leave blank to disable email alerts. Leads are always saved in the Leads tab.</p>
                </div>
                <div className="rounded-xl bg-[#fbefdf] border border-[#E6D7A2] p-4 space-y-3">
                  <label className="flex items-center justify-between gap-4">
                    <span className="text-[15px] font-medium text-[#1B1D21]">Send enquirers a confirmation email</span>
                    <input data-testid="confirmation-enabled-toggle" type="checkbox" checked={ads.confirmationEnabled !== false} onChange={(e) => setAds({ ...ads, confirmationEnabled: e.target.checked })} className="w-5 h-5 accent-[#e0564e]" />
                  </label>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#45596b] mb-1.5">Extra message (optional)</label>
                    <textarea data-testid="confirmation-message-input" value={ads.confirmationMessage || ''} onChange={(e) => setAds({ ...ads, confirmationMessage: e.target.value })} rows={3}
                      className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[14px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]" placeholder="e.g. We typically reply within one business day. In the meantime, explore our hotel guides!" />
                    <p className="mt-1 text-[12px] text-[#8a7a63]">Added to the friendly auto-confirmation sent to each person who submits an enquiry form.</p>
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-[#45596b] mb-1.5">AdSense client ID (ca-pub-…)</label>
                  <input value={ads.adsenseClient || ''} onChange={(e) => setAds({ ...ads, adsenseClient: e.target.value })}
                    className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]" placeholder="ca-pub-XXXXXXXXXXXXXXXX" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#45596b] mb-2">Ad unit slot IDs (from AdSense → Ad units). Leave blank to show placeholders.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {['header', 'inContent', 'inFeed', 'sidebar', 'footer'].map((k) => (
                      <div key={k}>
                        <label className="block text-[12px] text-[#8a7a63] mb-1 capitalize">{k}</label>
                        <input value={(ads.adSlots && ads.adSlots[k]) || ''} onChange={(e) => setAds({ ...ads, adSlots: { ...(ads.adSlots || {}), [k]: e.target.value } })}
                          className="w-full rounded-xl bg-white border border-[#E6D7A2] px-3 py-2 text-[14px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]" placeholder="e.g. 1234567890" />
                      </div>
                    ))}
                  </div>
                </div>
                {adsState.status === 'error' && <p className="text-[14px] text-[#d6453c]">{adsState.msg}</p>}
                {adsState.status === 'done' && <p className="text-[14px] text-[#2F663C] inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> {adsState.msg}</p>}
                <button type="submit" disabled={adsState.status === 'saving'} className="btn-sunset inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold disabled:opacity-70">
                  {adsState.status === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save ad settings
                </button>
              </form>
            )}
          </div>
        )}

        {/* Leads */}
        {tab === 'leads' && (
          <div>
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <p className="text-[15px] text-[#4B4F58]">{leads.length} {leads.length === 1 ? 'lead' : 'leads'} captured</p>
              <button onClick={downloadLeadsCsv} disabled={leads.length === 0} className="btn-sunset inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold disabled:opacity-50">
                <Download className="w-4 h-4" /> Download CSV
              </button>
            </div>
            {leadsLoading ? (
              <div className="py-16 flex justify-center text-[#92722A]"><Loader2 className="w-7 h-7 animate-spin" /></div>
            ) : leads.length === 0 ? (
              <div className="py-14 text-center rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2]"><p className="text-[16px] text-[#4B4F58]">No leads yet. Inquiry forms on Living, Business & Property will collect them here.</p></div>
            ) : (
              <div className="space-y-3">
                {leads.map((l) => (
                  <div key={l.id} className="flex items-start justify-between gap-4 rounded-2xl bg-white border border-[#E6D7A2] p-5 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)]">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold tracking-wide uppercase rounded-full bg-[#F2ECCF] text-[#8a5a2e] px-2.5 py-0.5">{l.type}</span>
                        <h3 className="font-display text-[18px] font-semibold text-[#1B1D21]">{l.name}</h3>
                      </div>
                      <p className="mt-1 text-[14px] text-[#2F663C]">{l.email}{l.phone ? ` · ${l.phone}` : ''}</p>
                      {l.message ? <p className="mt-1 text-[14px] text-[#4B4F58]">{l.message}</p> : null}
                    </div>
                    <button onClick={() => removeLead(l.id)} aria-label="Delete" className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full text-[#9aa7b3] hover:bg-[#e0564e] hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers */}
        {tab === 'subscribers' && (
          <div>
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <p className="text-[15px] text-[#4B4F58]">{subscribers.length} newsletter {subscribers.length === 1 ? 'subscriber' : 'subscribers'}</p>
              <button
                onClick={downloadCsv}
                disabled={subscribers.length === 0}
                className="btn-sunset inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Download CSV
              </button>
            </div>
            {subsLoading ? (
              <div className="py-16 flex justify-center text-[#92722A]"><Loader2 className="w-7 h-7 animate-spin" /></div>
            ) : subscribers.length === 0 ? (
              <div className="py-14 text-center rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2]">
                <p className="text-[16px] text-[#4B4F58]">No subscribers yet.</p>
              </div>
            ) : (
              <div className="rounded-2xl bg-white border border-[#E6D7A2] overflow-hidden shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)]">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#fbefdf] text-[#8a7a63] text-[12px] uppercase tracking-wide">
                      <th className="px-6 py-3 font-semibold">Email</th>
                      <th className="px-6 py-3 font-semibold">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E6D7A2]">
                    {subscribers.map((s, i) => (
                      <tr key={i} className="text-[15px] text-[#1B1D21]">
                        <td className="px-6 py-3">{s.email}</td>
                        <td className="px-6 py-3 text-[#4B4F58]">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Settings */}
        {tab === 'settings' && (
          <div className="max-w-lg">
            <div className="rounded-2xl bg-white border border-[#E6D7A2] p-7 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)]">
              <div className="flex items-center gap-2 text-[#e0564e]">
                <KeyRound className="w-5 h-5" />
                <span className="text-[12px] font-semibold tracking-[0.16em] uppercase">Account</span>
              </div>
              <h2 className="mt-2 font-display text-[24px] font-semibold text-[#1B1D21]">Change your password</h2>
              <form onSubmit={submitPassword} className="mt-5 space-y-4">
                {[
                  { k: 'current', l: 'Current password' },
                  { k: 'next', l: 'New password (min 6 characters)' },
                  { k: 'confirm', l: 'Confirm new password' },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="block text-[13px] font-semibold text-[#45596b] mb-1.5">{f.l}</label>
                    <input
                      type="password"
                      value={cp[f.k]}
                      onChange={(e) => setCp((p) => ({ ...p, [f.k]: e.target.value }))}
                      required
                      className="w-full rounded-xl bg-white border border-[#E6D7A2] px-4 py-2.5 text-[15px] text-[#1B1D21] outline-none focus:ring-2 focus:ring-[#e0564e]"
                    />
                  </div>
                ))}
                {cpState.status === 'error' && <p className="text-[14px] text-[#d6453c]">{cpState.msg}</p>}
                {cpState.status === 'done' && <p className="text-[14px] text-[#2F663C] inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> {cpState.msg}</p>}
                <button
                  type="submit"
                  disabled={cpState.status === 'saving'}
                  className="btn-sunset inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold disabled:opacity-70"
                >
                  {cpState.status === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update password
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <EditModal
          title={`${modal.item ? 'Edit' : 'Add'} ${titleFor[modal.entity].toLowerCase()}`}
          fields={fieldsFor[modal.entity]}
          initial={modal.item}
          onClose={() => setModal(null)}
          onSave={(payload) => onSave(modal.entity, modal.item, payload)}
        />
      )}
    </div>
  );
};

function Row({ title, subtitle, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-white border border-[#E6D7A2] p-5 shadow-[0_14px_34px_-28px_rgba(27,29,33,0.45)]">
      <div className="min-w-0">
        <h3 className="font-display text-[18px] font-semibold text-[#1B1D21] truncate">{title}</h3>
        <p className="mt-1 text-[14px] text-[#4B4F58] truncate">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onEdit} aria-label="Edit" className="w-9 h-9 inline-flex items-center justify-center rounded-full text-[#2F663C] hover:bg-[#2F663C] hover:text-white transition-colors">
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={onDelete} aria-label="Delete" className="w-9 h-9 inline-flex items-center justify-center rounded-full text-[#9aa7b3] hover:bg-[#e0564e] hover:text-white transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default AdminDashboard;
